import { useState, useEffect } from "react";
import { toast } from "sonner";
import { eur, g, p as post, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COOKINGS = ["al sangue", "media", "ben cotta"];
const BASI = [["base pomodoro", "Pomodoro", 4.5], ["base bianca", "Bianca", 5], ["base pomodoro e mozzarella", "Pomodoro e mozzarella", 5]];
const num = (v) => Number(String(v).replace(",", ".")) || 0;

/** Aggiunta guidata: ingredienti extra della pizza, grammatura al chilo, cottura e opzioni di riga. */
export const AggiungiPiatto = ({ item, onClose, onAdd, ingredienti = [], onSaved }) => {
  const { t } = useI18n();
  const { settings, demoMode } = useApp();
  const [extras, setExtras] = useState([]);
  const [levati, setLevati] = useState([]);
  const [liberoNome, setLiberoNome] = useState("");
  const [liberoPrezzo, setLiberoPrezzo] = useState("0,00");
  const [grams, setGrams] = useState(200);
  const [cooking, setCooking] = useState("");
  const [takeaway, setTakeaway] = useState(false);
  const [toCut, setToCut] = useState(false);
  const [firmaNome, setFirmaNome] = useState("");
  const [firmaApri, setFirmaApri] = useState(false);
  const [prezzoRiga, setPrezzoRiga] = useState("");
  const [scelte, setScelte] = useState({});          // scelte obbligatorie: naturale/frizzante…
  const [libere, setLibere] = useState({});          // il tipo scritto a mano (amari, liquori)
  const [ingReparto, setIngReparto] = useState(null);

  useEffect(() => {
    setExtras([]);
    setLevati([]);
    setGrams(200);
    setCooking(item?.needs_cooking ? "media" : "");
    setTakeaway(false);
    setToCut(false);
    setFirmaNome("");
    setFirmaApri(false);
    setPrezzoRiga("");
    setScelte({});
    setLibere({});
    // Sulla pizza non serve il prosecco: si chiedono solo gli ingredienti del suo reparto
    setIngReparto(null);
    if (item && (item.tags || []).includes("componi") && item.department) {
      g("/menu/ingredienti", { reparto: item.department })
        .then((r) => setIngReparto(r))
        .catch(() => setIngReparto([]));
    }
  }, [item]);

  if (!item) return null;

  const available = item.extras || [];
  const componi = (item.tags || []).includes("componi");
  // Prezzo di partenza correggibile (es. «Pizza novità»): si batte il prezzo del giorno
  const prezzoAperto = (item.tags || []).includes("prezzo-aperto");
  // Scelte obbligatorie: acqua naturale o frizzante, vino bianco o rosso, quale amaro
  const varianti = item.variants || [];
  const manca = varianti.find((v) => (v.libera ? !(libere[v.name] || "").trim() : !scelte[v.name]));
  // Gli ingredienti stanno nella descrizione del piatto, come sul menu di carta
  const ingDelPiatto = (item.description || "")
    .split(/[,;·]/)
    .map((x) => x.trim())
    .filter((x) => x && x.length < 28)
    .slice(0, 12);
  const extraTotal = extras.reduce((s, e) => s + e.amount, 0);
  const price = item.by_weight && item.price_kg ? (item.price_kg * (Number(grams) || 0)) / 1000 : item.price;
  const toggle = (e) =>
    setExtras((cur) => (cur.some((x) => x.name === e.name) ? cur.filter((x) => x.name !== e.name) : [...cur, e]));

  // Basi e ingredienti della pizza inventata: prezzi impostati dal titolare nel Menu
  const basi = (settings?.pizza_bases?.length ? settings.pizza_bases : BASI.map(([, n, a]) => ({ name: n, amount: a })));
  const formati = settings?.pizza_formats?.length ? settings.pizza_formats : [];
  const impasti = settings?.pizza_doughs?.length ? settings.pizza_doughs : [];
  const ingPrezzo = Number(settings?.ingredient_price ?? 1.0);
  const conPrezzo = new Map(available.map((e) => [e.name.toLowerCase(), Number(e.amount) || 0]));
  // Se il reparto non dà niente (piatti senza reparto) si torna all'elenco completo: meglio tutti
  // che nessuno.
  const listaIng = componi ? (ingReparto?.length ? ingReparto : (ingReparto === null ? [] : ingredienti)) : ingredienti;
  // Prima il listino del locale coi suoi prezzi, poi gli altri ingredienti al prezzo base
  const dalListino = available.map((e) => ({ name: e.name, amount: Number(e.amount) || 0 }));
  // Niente doppioni col listino: «mozzarella di bufala» non deve costare meno di «Bufala»
  const paroleListino = dalListino.flatMap((e) => e.name.toLowerCase().split(/[\s/()]+/))
    .filter((w) => w.length > 3);
  const altriIng = listaIng
    .filter((n) => !conPrezzo.has(n.toLowerCase()))
    .filter((n) => !paroleListino.some((w) => n.toLowerCase().includes(w)))
    .map((n) => ({ name: n, amount: ingPrezzo }));
  const scelta = [...dalListino, ...altriIng];
  const totale = price + extraTotal;
  const forzato = num(prezzoRiga);
  const usaForzato = (componi || prezzoAperto) && prezzoRiga.trim() !== "" && Math.abs(forzato - totale) > 0.004;
  const finale = usaForzato ? forzato : totale;

  const confirm = () => {
    if (item.by_weight && (!Number(grams) || Number(grams) <= 0)) return toast.error(t("x.weight.needGrams"));
    if (manca) return toast.error(`Scegli: ${manca.name}`);
    // Le scelte viaggiano come righe senza prezzo: cucina e bar le leggono sotto al piatto
    const dalleScelte = varianti.map((v) => ({
      name: v.libera ? (libere[v.name] || "").trim() : scelte[v.name],
      amount: 0,
    })).filter((x) => x.name);
    onAdd({
      // Prezzo scritto a mano: gli ingredienti restano scritti per il pizzaiolo, ma non si sommano
      extras: [...(usaForzato ? extras.map((e) => ({ ...e, amount: 0 })) : extras), ...dalleScelte],
      surcharge: usaForzato ? Number((forzato - price).toFixed(2)) : 0,
      surchargeLabel: usaForzato ? "prezzo concordato" : "",
      removed: levati, grams: item.by_weight ? Number(grams) : 0, cooking, takeaway, to_cut: toCut,
    });
    onClose();
  };

  // La pizza che si è inventato il cliente resta in listino col suo nome: la volta dopo c'è già.
  const salvaFirma = async () => {
    const nome = firmaNome.trim();
    if (!nome) return toast.error("Scrivi il nome da dare alla pizza");
    try {
      await post("/menu/firma", {
        name: nome, base_id: item.id, price: Number(finale.toFixed(2)),
        removed: levati, added: extras.map((e) => e.name), customer: nome,
      });
      toast.success(`«${nome}» è in listino`, { description: "La trovi nella categoria Le nostre firme" });
      setFirmaApri(false);
      setFirmaNome("");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="add-options-dialog">
        <DialogHeader>
          <DialogTitle>{item.name}</DialogTitle>
        </DialogHeader>

        {varianti.length > 0 && (
          <div className="rounded-md border-2 border-sky-300 bg-sky-50 p-3" data-testid="varianti-block">
            {varianti.map((v) => (
              <div key={v.name} className="mb-2 last:mb-0">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-900">
                  {v.name} <span className="font-normal normal-case text-sky-700">· scelta obbligatoria</span>
                </p>
                {v.libera ? (
                  <Input
                    className="mt-2 h-11 bg-white"
                    placeholder="Scrivi quale (es. Montenegro, Limoncello)"
                    value={libere[v.name] || ""}
                    onChange={(e) => setLibere((c) => ({ ...c, [v.name]: e.target.value }))}
                    data-testid={`variante-libera-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                  />
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {v.options.map((o) => (
                      <button
                        key={o}
                        data-testid={`variante-${o.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => setScelte((c) => ({ ...c, [v.name]: c[v.name] === o ? "" : o }))}
                        className={`h-11 rounded-md border px-3 text-sm font-semibold ${
                          scelte[v.name] === o ? "border-sky-700 bg-sky-700 text-white" : "border-sky-200 bg-white"
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {prezzoAperto && (
          <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3" data-testid="prezzo-aperto-block">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-900">Il prezzo di oggi</p>
            <p className="mt-1 text-[11px] text-amber-900/80">
              In listino c'è {eur(item.price)}: se la novità di stasera costa diverso, scrivilo qui.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <Input className="h-11 w-28 bg-white text-center" placeholder={item.price.toFixed(2).replace(".", ",")}
                     value={prezzoRiga} onChange={(e) => setPrezzoRiga(e.target.value)}
                     data-testid="prezzo-aperto-input" />
              {usaForzato && (
                <span className="text-[11px] font-bold uppercase text-amber-800" data-testid="prezzo-aperto-attivo">
                  prezzo concordato
                </span>
              )}
            </div>
          </div>
        )}

        {item.by_weight && (
          <div className="rounded-md border border-slate-200 p-3" data-testid="weight-block">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("x.weight.title")}</p>
            <p className="mt-1 text-[11px] text-slate-500">{t("x.weight.perKg", { amount: eur(item.price_kg) })}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[150, 200, 250, 300, 400, 500].map((n) => (
                <button
                  key={n}
                  data-testid={`grams-${n}`}
                  onClick={() => setGrams(n)}
                  className={`h-10 rounded-md border px-3 text-sm font-bold ${Number(grams) === n ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`}
                >
                  {n}g
                </button>
              ))}
              <Input className="h-10 w-24" type="number" min="10" step="10" value={grams} onChange={(e) => setGrams(e.target.value)} data-testid="grams-input" />
            </div>
            <p className="font-num mt-2 text-sm font-bold" data-testid="weight-price">{t("x.weight.price", { amount: eur(price) })}</p>
          </div>
        )}

        {item.needs_cooking && (
          <div className="rounded-md border border-slate-200 p-3" data-testid="cooking-block">
            <Label className="text-xs">{t("x.weight.cooking")}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {COOKINGS.map((c) => (
                <button
                  key={c}
                  data-testid={`cooking-${c.replace(/\s+/g, "-")}`}
                  onClick={() => setCooking(c)}
                  className={`h-10 rounded-md border px-3 text-sm font-semibold ${cooking === c ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {impasti.length > 0 && (componi || item.category === "Pizze") && (
          <div data-testid="impasto-block">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">L'impasto</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {impasti.map((d) => {
                const id = `impasto ${d.name.toLowerCase()}`;
                const on = extras.some((x) => x.name === id);
                return (
                  <button
                    key={d.name}
                    data-testid={`impasto-${d.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() =>
                      setExtras((c) => [...c.filter((x) => !x.name.startsWith("impasto ")),
                                        ...(on ? [] : [{ name: id, amount: Number(d.amount) || 0 }])])
                    }
                    className={`h-10 rounded-md border px-3 text-sm font-semibold ${
                      on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"
                    }`}
                  >
                    {d.name} {!!d.amount && <span className="font-num opacity-70">+{eur(d.amount)}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {componi && (
          <div className="rounded-md border-2 border-red-200 bg-red-50 p-3" data-testid="componi-block">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-red-900">La base</p>
            <p className="mt-1 text-[11px] text-red-900/80">
              La pizza inventata dal cliente: scegli la base e il formato, poi tocca gli ingredienti
              che ti dice. Il prezzo si somma da solo.
            </p>
            {demoMode && (
              <p className="mt-2 rounded-md bg-red-100 p-2 text-[11px] font-semibold text-red-900"
                 data-testid="componi-prezzi-esempio">
                Prezzi di esempio, solo per la demo: nel tuo locale metti i tuoi.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {basi.map((b) => {
                const id = `base ${b.name.toLowerCase()}`;
                const on = extras.some((x) => x.name === id);
                return (
                  <button
                    key={b.name}
                    data-testid={`base-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() =>
                      setExtras((c) => [...c.filter((x) => !x.name.startsWith("base ")),
                                        ...(on ? [] : [{ name: id, amount: Number(b.amount) || 0 }])])
                    }
                    className={`h-10 rounded-md border px-3 text-sm font-semibold ${
                      on ? "border-red-600 bg-red-600 text-white" : "border-red-200 bg-white"
                    }`}
                  >
                    {b.name} {b.amount > 0 && <span className="font-num opacity-70">{eur(b.amount)}</span>}
                  </button>
                );
              })}
            </div>

            {formati.length > 0 && (
              <>
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.16em] text-red-900">Il formato</p>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="componi-formati">
                  {formati.map((f) => {
                    const id = `formato ${f.name.toLowerCase()}`;
                    const on = extras.some((x) => x.name === id);
                    return (
                      <button
                        key={f.name}
                        data-testid={`formato-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() =>
                          setExtras((c) => [...c.filter((x) => !x.name.startsWith("formato ")),
                                            ...(on ? [] : [{ name: id, amount: Number(f.amount) || 0 }])])
                        }
                        className={`h-10 rounded-md border px-3 text-sm font-semibold ${
                          on ? "border-red-600 bg-red-600 text-white" : "border-red-200 bg-white"
                        }`}
                      >
                        {f.name} {!!f.amount && <span className="font-num opacity-70">{eur(f.amount)}</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <p className="mt-4 text-xs font-bold uppercase tracking-[0.16em] text-red-900">Gli ingredienti del listino</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="componi-ingredienti">
              {dalListino.map((e) => {
                const on = extras.some((x) => x.name === e.name);
                return (
                  <button
                    key={e.name}
                    data-testid={`ing-${e.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggle(e)}
                    className={`h-10 rounded-md border px-3 text-sm font-semibold ${
                      on ? "border-emerald-600 bg-emerald-600 text-white" : "border-red-200 bg-white"
                    }`}
                  >
                    {e.name} <span className="font-num opacity-70">{eur(e.amount)}</span>
                  </button>
                );
              })}
              {scelta.length === 0 && (
                <p className="text-[11px] text-red-900/80">
                  Nessun ingrediente in elenco: scrivilo qui sotto, o impostali dal Menu (Aggiunte per reparto).
                </p>
              )}
            </div>

            {altriIng.length > 0 && (
              <>
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.16em] text-red-900/70">
                  Altri ingredienti · {eur(ingPrezzo)} l'uno
                </p>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="componi-altri-ingredienti">
                  {altriIng.map((e) => {
                    const on = extras.some((x) => x.name === e.name);
                    return (
                      <button
                        key={e.name}
                        data-testid={`ing-${e.name.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => toggle(e)}
                        className={`h-10 rounded-md border px-3 text-sm font-medium ${
                          on ? "border-emerald-600 bg-emerald-600 text-white" : "border-red-100 bg-white/70 text-slate-700"
                        }`}
                      >
                        {e.name} <span className="font-num opacity-60">{eur(e.amount)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-red-200 pt-3">
              <span className="font-num text-base font-bold text-red-900" data-testid="componi-totale">
                {eur(finale)}
              </span>
              <span className="text-[11px] text-red-900/80">prezzo diverso? (lo puoi fare anche in cassa)</span>
              <Input className="h-10 w-24 bg-white text-center" placeholder={totale.toFixed(2).replace(".", ",")}
                     value={prezzoRiga} onChange={(e) => setPrezzoRiga(e.target.value)}
                     data-testid="componi-prezzo" />
              {usaForzato && (
                <span className="text-[11px] font-bold uppercase text-red-700" data-testid="componi-prezzo-forzato">
                  prezzo concordato
                </span>
              )}
            </div>
          </div>
        )}

        {ingDelPiatto.length > 0 && (
          <div className="rounded-md border border-slate-200 p-3" data-testid="togli-block">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Togli un ingrediente</p>
            <p className="mt-1 text-[11px] text-slate-500">
              Tocca quello che il cliente non vuole: in cucina arriva scritto in rosso.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {ingDelPiatto.map((ing) => {
                const off = levati.includes(ing);
                return (
                  <button
                    key={ing}
                    data-testid={`togli-${ing.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setLevati((c) => (off ? c.filter((x) => x !== ing) : [...c, ing]))}
                    className={`h-10 rounded-md border px-3 text-sm font-semibold ${
                      off ? "border-rose-600 bg-rose-50 text-rose-700 line-through" : "border-slate-200"
                    }`}
                  >
                    {ing}
                  </button>
                );
              })}
            </div>
            {levati.length > 0 && (
              <p className="mt-2 text-[11px] font-bold uppercase text-rose-600" data-testid="togli-riepilogo">
                senza {levati.join(", ")}
              </p>
            )}
          </div>
        )}

        {available.length > 0 && !componi && (
          <div className="rounded-md border border-slate-200 p-3" data-testid="extras-block">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{t("x.extra.title")}</p>
            <p className="mt-1 text-[11px] text-slate-500">Prezzi già pronti: tocca l'aggiunta che serve.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {available.map((e) => {
                const on = extras.some((x) => x.name === e.name);
                return (
                  <button
                    key={e.name}
                    data-testid={`extra-${e.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggle(e)}
                    className={`h-10 rounded-md border px-3 text-sm font-semibold ${on ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200"}`}
                  >
                    {e.name} <span className="font-num text-[11px] text-slate-500">{eur(e.amount)}</span>
                  </button>
                );
              })}
            </div>
            <p className="font-num mt-2 text-[11px] text-slate-500" data-testid="extras-total">
              {extras.length ? `+ ${eur(extraTotal)}` : t("x.extra.none")}
            </p>
          </div>
        )}

        <div className="rounded-md border border-dashed border-slate-300 p-3" data-testid="extra-libero-block">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            {componi ? "Gli ingredienti della pizza" : "Aggiungi un ingrediente"}
          </p>
          <p className="mt-1 text-[11px] text-slate-500">
            Scegli dall'elenco del locale o scrivilo a mano. Lascia 0,00 se non si paga (per esempio
            i gusti di un mezzo metro), altrimenti metti il prezzo.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Input className="h-10 w-40" placeholder="scegli o scrivi" value={liberoNome} list="rh-ingredienti"
                   onChange={(e) => setLiberoNome(e.target.value)} data-testid="extra-libero-nome" />
            <datalist id="rh-ingredienti">
              {ingredienti.map((i) => (
                <option key={i} value={i} />
              ))}
            </datalist>
            <Input className="h-10 w-20 text-center" placeholder="0,00" value={liberoPrezzo}
                   onChange={(e) => setLiberoPrezzo(e.target.value)} data-testid="extra-libero-importo" />
            <Button
              variant="outline"
              className="h-10"
              data-testid="extra-libero-add"
              onClick={() => {
                const n = liberoNome.trim();
                if (!n) return;
                setExtras((c) => [...c, { name: n, amount: Number(String(liberoPrezzo).replace(",", ".")) || 0 }]);
                setLiberoNome("");
                toast.success(`«${n}» aggiunto alla riga`);
              }}
            >
              Metti
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            data-testid="opt-takeaway"
            onClick={() => setTakeaway((v) => !v)}
            className={`h-10 rounded-md border px-3 text-sm font-semibold ${takeaway ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`}
          >
            {t("x.opt.takeaway")}
          </button>
          <button
            data-testid="opt-to-cut"
            onClick={() => setToCut((v) => !v)}
            className={`h-10 rounded-md border px-3 text-sm font-semibold ${toCut ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`}
          >
            {t("x.opt.cut")}
          </button>
        </div>

        {(extras.length > 0 || levati.length > 0) && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="firma-block">
            {firmaApri ? (
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-900">
                  Nome della pizza del cliente
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Input className="h-10 w-44" autoFocus placeholder="es. La Fabio" value={firmaNome}
                         onChange={(e) => setFirmaNome(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && salvaFirma()}
                         data-testid="firma-nome" />
                  <Button className="h-10" onClick={salvaFirma} data-testid="firma-salva">Salva in listino</Button>
                  <Button variant="ghost" className="h-10" onClick={() => setFirmaApri(false)} data-testid="firma-annulla">
                    Annulla
                  </Button>
                </div>
                <p className="text-[11px] text-amber-900/80">
                  Va nella categoria «Le nostre firme» a {eur(finale)} con gli ingredienti di adesso.
                </p>
              </div>
            ) : (
              <Button variant="outline" className="h-10 w-full border-amber-300 text-amber-900"
                      onClick={() => setFirmaApri(true)} data-testid="firma-apri">
                Salva come pizza del cliente
              </Button>
            )}
          </div>
        )}

        <Button className="h-12 w-full" onClick={confirm} data-testid="confirm-add-options-btn"
                disabled={!!manca}>
          {manca ? `Scegli: ${manca.name}` : `${t("x.weight.add")} · ${eur(finale)}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

/** Annullo di un piatto con motivo: finisce nel registro sprechi del titolare. */
export const AnnullaPiatto = ({ item, reasons, onClose, onConfirm }) => {
  const { t } = useI18n();
  const [reason, setReason] = useState("");

  useEffect(() => {
    setReason(reasons?.[0] || "altro");
  }, [item, reasons]);

  if (!item) return null;

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cancel-item-dialog">
        <DialogHeader>
          <DialogTitle>{t("x.cancel.title", { name: item.name })}</DialogTitle>
        </DialogHeader>
        <Label className="text-xs">{t("x.cancel.reason")}</Label>
        <div className="flex flex-wrap gap-2">
          {(reasons || []).map((r) => (
            <button
              key={r}
              data-testid={`cancel-reason-${r.replace(/\s+/g, "-")}`}
              onClick={() => setReason(r)}
              className={`h-10 rounded-md border px-3 text-sm font-semibold ${reason === r ? "border-rose-600 bg-rose-50 text-rose-700" : "border-slate-200"}`}
            >
              {r}
            </button>
          ))}
        </div>
        <Button className="h-12 w-full bg-rose-600 hover:bg-rose-700" onClick={() => onConfirm(reason)} data-testid="confirm-cancel-item-btn">
          {t("x.cancel.confirm")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
